<script setup lang="ts">
const props = withDefaults(defineProps<{
  interactive?: boolean
  presentationConnected?: boolean
}>(), {
  interactive: true,
  presentationConnected: false,
})

const store = useScoreboardStore()
const { data: ui } = useScoreboardUI()

const hasTimer = computed(() => store.config.timerMode !== 'none')

// Format seconds as `s`, `m:ss`, or `h:mm:ss` so a long count-up
// session reads naturally without overflowing the chip.
const timerDisplay = computed(() => {
  const s = Math.max(0, store.timerSeconds)
  const total = Math.floor(s)
  const hh = Math.floor(total / 3600)
  const mm = Math.floor((total % 3600) / 60)
  const sec = total % 60
  if (hh > 0) {
    return `${hh}:${mm.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`
  }
  if (mm > 0) {
    return `${mm}:${sec.toString().padStart(2, '0')}`
  }
  return String(sec)
})

const isCountdown = computed(() => store.config.timerMode === 'countdown')
const isExpired = computed(() => isCountdown.value && store.timerSeconds === 0)
const isWarning = computed(() =>
  isCountdown.value && store.timerSeconds > 0 && store.timerSeconds <= 10,
)

const teamCount = computed(() => store.teams.length)

// Team grid layout. Landscape-first picks that keep the boxes large
// on a TV. With one team we fall back to a single full-width row;
// past that the column count grows with team count, capped at 4 so
// a row never gets too narrow to read.
const teamGridStyle = computed(() => {
  const n = teamCount.value
  let cols: number
  if (n <= 2) {
    cols = n // 1 or 2 across
  } else if (n <= 3) {
    cols = 3
  } else if (n <= 4) {
    cols = 2 // 2x2 reads better than 4 across
  } else if (n <= 6) {
    cols = 3
  } else {
    cols = 4 // 7 or 8 → 4 across, 2 rows
  }
  const rows = Math.ceil(n / cols)
  return {
    display: 'grid',
    // Gap between cards scales with the viewport so the chrome
    // breathes as the stage gets larger.
    gap: 'clamp(0.75rem, 2vw, 2rem)',
    gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
  } as const
})

// ── Tap handling ────────────────────────────────────────
// Single tap = +1, double tap = -1. Same debounce pattern as the
// Showdown TeamScoreboard so the single-tap handler never fires on
// the first tap of a double-tap gesture.
const tapTimers: (null | ReturnType<typeof setTimeout>)[] = []
const tapCounts: number[] = []

function handleTeamTap(i: number) {
  if (!props.interactive) {
    return
  }
  tapCounts[i] = (tapCounts[i] ?? 0) + 1
  if (tapTimers[i]) {
    clearTimeout(tapTimers[i]!)
  }
  tapTimers[i] = setTimeout(() => {
    const count = tapCounts[i] ?? 0
    tapCounts[i] = 0
    tapTimers[i] = null
    if (count >= 2) {
      store.decrementScore(i)
    } else {
      store.incrementScore(i)
    }
  }, 280)
}

function handleTimerTap() {
  if (!props.interactive) {
    return
  }
  store.toggleTimer()
}
</script>

<template>
  <div class="sfg-scoreboard-stage relative flex h-screen w-screen flex-col overflow-hidden p-[clamp(1.25rem,4vw,4rem)] gap-[clamp(1rem,3vw,3rem)]">
    <!-- Timer bar (top). Absent entirely when the leader picked No
         Timer at setup; otherwise occupies a fixed slice of the
         stage and stays put even when paused. -->
    <div
      v-if="hasTimer"
      v-click="props.interactive ? handleTimerTap : undefined"
      :data-vc-state-active="undefined"
      :class="[
        'timer-bar relative mx-auto flex shrink-0 items-center justify-center rounded-2xl select-none touch-none',
        // Capped width keeps the timer chip a focal point at the top
        // instead of letting it stretch the full stage; the stage
        // padding plus mx-auto centers it under the page chrome.
        'w-full max-w-[50vw] h-[14vh] sm:h-[16vh]',
        props.interactive ? 'cursor-pointer' : 'cursor-default',
        isWarning ? 'bg-red-600/85 ring-2 ring-red-300' : 'bg-white/5 ring-2 ring-white/15',
        'data-vc-state-active:brightness-90',
      ]"
    >
      <!-- Big readout. Always the formatted time; the bottom-right hint
           switches to "Tap to restart" once the countdown lands on 0. -->
      <span
        :key="store.timerSeconds"
        :class="[
          'timer-text font-black tabular-nums leading-none',
          isExpired ? 'text-red-300' : 'text-white',
          isWarning ? 'animate-timer-tick' : '',
        ]"
      >
        {{ timerDisplay }}
      </span>
      <!-- Action icon sits in the bottom-right of the bar whenever
           the timer is stopped. There's no pause/resume concept in
           this game; tapping a stopped timer always resets to the
           starting point and runs fresh, so a single restart glyph
           covers both manually stopped and countdown-finished.
           Hidden on the presenter view (audience doesn't tap, and
           the icon would imply an action the presenter can't take). -->
      <UIcon
        v-if="!store.timerRunning && props.interactive"
        name="mdi:refresh"
        class="absolute bottom-2 right-3 text-white/70"
        :style="{ width: 'clamp(1.5rem, 3vw, 3rem)', height: 'clamp(1.5rem, 3vw, 3rem)' }"
        :aria-label="ui?.gameScreen.timerStoppedHint ?? 'Tap to restart'"
      />
    </div>

    <!-- Team grid (fills remaining height) -->
    <div
      :style="teamGridStyle"
      class="flex-1 min-h-0"
    >
      <div
        v-for="(team, i) in store.teams"
        :key="i"
        v-click="() => handleTeamTap(i)"
        :data-vc-state-active="undefined"
        :style="{ backgroundColor: team.color }"
        :class="[
          'team-box relative flex items-center justify-between gap-3 rounded-2xl shadow-lg overflow-hidden',
          'select-none touch-none',
          props.interactive ? 'cursor-pointer' : 'cursor-default',
          'data-vc-state-active:brightness-75',
        ]"
      >
        <span class="team-name min-w-0 flex-1 truncate font-bold text-white drop-shadow">{{ team.name }}</span>
        <span class="team-score font-black text-white drop-shadow tabular-nums">{{ team.score }}</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
.sfg-scoreboard-stage {
  /* Same dark slate as Sword Drill so the games feel coherent. */
  background: radial-gradient(
    ellipse at center,
    rgb(30 41 59) 0%,
    rgb(15 23 42) 70%,
    rgb(2 6 23) 100%
  );
  color: white;
}

/* Timer chip uses container-query sizing so the digits scale with
   the chip across screen sizes without springing the layout. */
.timer-bar {
  container-type: size;
  container-name: timerbar;
}
.timer-text {
  font-size: clamp(2rem, min(85cqh, 22cqi), 9rem);
}

/* Each team box is its own size-container (mirrors Showdown's
   TeamScoreboard pattern) so name + score both scale to the box's
   actual rendered footprint, not the viewport. */
.team-box {
  container-type: size;
  container-name: team;
  /* Inner padding scales with the box so the colored chrome holds
     a generous cushion around the text at any team count. */
  padding-inline: clamp(0.75rem, 5cqi, 2.75rem);
  padding-block: clamp(0.5rem, 5cqh, 1.75rem);
}
.team-name {
  /* Larger floor and bigger axis caps so the team name reads from
     the back of the room. Caps are still bounded by the smaller of
     cqh/cqi so neither dimension alone can blow up the type. */
  font-size: clamp(1.5rem, min(45cqh, 18cqi), 5rem);
  line-height: 1.05;
}
.team-score {
  /* The score is the focal point of each box. Push the cqh / cqi
     caps so digits fill the box at any team count without
     overflowing the colored edge. */
  font-size: clamp(2rem, min(90cqh, 36cqi), 9rem);
  line-height: 1;
}

@keyframes timer-tick {
  0%   { opacity: 1; text-shadow: 0 0 22px rgba(255,255,255,0.85); }
  70%  { opacity: 0.9; text-shadow: none; }
  100% { opacity: 0.6; text-shadow: none; }
}
.animate-timer-tick {
  animation: timer-tick 1s linear forwards;
}
</style>
